import React, { useState } from "react";
import { Box, Typography, Collapse, Link, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { AGENT_FILE_NAMES } from "../../apis/settingsApis/policy-apis/policyApis";

const SMARTSCREEN_DOC_URL =
  "https://learn.microsoft.com/en-us/windows/apps/package-and-deploy/smartscreen-reputation";

// Taken from the download map rather than written out again: the illustration
// shows this as the app name and users compare it against the file they just
// downloaded, so a second copy would silently go stale on the next version bump.
const INSTALLER_NAME = AGENT_FILE_NAMES.windows;
const PUBLISHER_NAME = "Solsphere AI Inc";

const WHAT_TO_DO = [
  <>
    Click <strong>More info</strong> on the blue screen.
  </>,
  <>
    Confirm the <strong>Publisher</strong> reads <strong>{PUBLISHER_NAME}</strong> — this is the
    signature on the genuine installer.
  </>,
  <>
    Click <strong>Run anyway</strong> to continue the installation.
  </>,
];

/**
 * Illustration of the Microsoft Defender SmartScreen prompt users hit on the first
 * Windows release, drawn inline so it stays crisp at any width and ships no binary.
 */
const SmartScreenIllustration = () => (
  <Box
    component="svg"
    viewBox="0 0 640 400"
    role="img"
    aria-label={`Microsoft Defender SmartScreen prompt reading "Windows protected your PC" for ${INSTALLER_NAME}, published by ${PUBLISHER_NAME}, with "Run anyway" and "Don't run" buttons.`}
    sx={{
      width: "100%",
      height: "auto",
      display: "block",
      borderRadius: "8px",
      fontFamily: '"Segoe UI", system-ui, -apple-system, sans-serif',
    }}
  >
    <rect width="640" height="400" fill="#0078D7" />

    {/* Window close button */}
    <rect x="576" y="0" width="64" height="40" fill="#1184DE" />
    <path
      d="M600 14 L616 30 M616 14 L600 30"
      stroke="#FFFFFF"
      strokeWidth="1.6"
      strokeLinecap="round"
    />

    <text x="28" y="86" fill="#FFFFFF" fontSize="31" fontWeight="400">
      Windows protected your PC
    </text>

    <text x="28" y="134" fill="#EAF3FC" fontSize="14.5">
      Microsoft Defender SmartScreen prevented an unrecognized app from
    </text>
    <text x="28" y="157" fill="#EAF3FC" fontSize="14.5">
      starting. Running this app might put your PC at risk.
    </text>

    <text x="28" y="205" fill="#EAF3FC" fontSize="14.5">
      App:
    </text>
    <text x="118" y="205" fill="#FFFFFF" fontSize="14.5">
      {INSTALLER_NAME}
    </text>

    <text x="28" y="240" fill="#EAF3FC" fontSize="14.5">
      Publisher:
    </text>
    <text x="118" y="234" fill="#FFFFFF" fontSize="14.5">
      19901, US, Delaware, Dover, 8 The Green Stree
    </text>
    <text x="118" y="256" fill="#FFFFFF" fontSize="14.5">
      t, {PUBLISHER_NAME}, {PUBLISHER_NAME}
    </text>

    {/* Callout pointing at the button users need */}
    <text x="326" y="306" fill="#FFD666" fontSize="13" fontWeight="600">
      Click this
    </text>
    <path
      d="M392 300 L410 300 L410 322"
      stroke="#FFD666"
      strokeWidth="1.6"
      fill="none"
      strokeLinecap="round"
    />
    <path d="M406 316 L410 324 L414 316 Z" fill="#FFD666" />

    <rect
      x="322"
      y="328"
      width="148"
      height="48"
      fill="none"
      stroke="#FFD666"
      strokeWidth="2.5"
      rx="4"
    />
    <rect x="328" y="334" width="136" height="36" fill="#FFFFFF" />
    <text x="396" y="357" fill="#1A1A1A" fontSize="14.5" fontWeight="600" textAnchor="middle">
      Run anyway
    </text>

    <rect x="484" y="334" width="128" height="36" fill="#FFFFFF" />
    <text x="548" y="357" fill="#1A1A1A" fontSize="14.5" textAnchor="middle">
      Don't run
    </text>
  </Box>
);

/**
 * Explains the SmartScreen warning shown for the first Windows release of the agent,
 * which has not yet built up Microsoft's reputation score.
 */
const WindowsSmartScreenNotice = () => {
  const theme = useTheme();
  // Collapsed by default: it only matters once a Windows download has actually
  // hit the SmartScreen prompt, so expanded it pushed the enrollment steps down
  // for everyone else. The heading stays visible for anyone who needs it.
  const [open, setOpen] = useState(false);
  const accent = theme.customColors.status.warning.text;

  return (
    <Box
      sx={{
        mt: 2,
        border: `1px solid ${alpha(accent, 0.35)}`,
        backgroundColor: theme.customColors.status.warning.bg,
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      <Box
        onClick={() => setOpen((v) => !v)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 2.5,
          py: 1.75,
          cursor: "pointer",
          userSelect: "none",
        }}
      >
        <ShieldOutlinedIcon sx={{ fontSize: 19, color: accent, flexShrink: 0 }} />
        <Typography
          sx={{ fontWeight: 600, color: accent, fontSize: "0.9375rem", flex: 1 }}
        >
          Windows only — you might see a "Windows protected your PC" screen. This is expected.
        </Typography>
        <ExpandMoreIcon
          sx={{
            fontSize: 22,
            color: accent,
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform 0.2s",
            flexShrink: 0,
          }}
        />
      </Box>

      <Collapse in={open}>
        <Box sx={{ px: 2.5, pb: 2.5 }}>
          <Typography
            variant="body2"
            sx={{ color: theme.customColors.text.secondary, lineHeight: 1.65, mb: 2 }}
          >
            Rego WatchTower is code-signed by <strong>{PUBLISHER_NAME}</strong>, but this is our
            first Windows release, so Microsoft Defender SmartScreen has not yet built up a
            reputation score for the new certificate. The warning reflects how many times the
            installer has been seen before — not a problem with the file. It disappears on its own
            as more devices install the agent.
          </Typography>

          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: theme.customColors.text.strong, mb: 1 }}
          >
            What to do
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, mb: 2 }}>
            {WHAT_TO_DO.map((step, i) => (
              <Box key={i} sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}>
                <Box
                  sx={{
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    backgroundColor: alpha(accent, 0.15),
                    color: accent,
                    fontSize: "0.6875rem",
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    mt: 0.25,
                  }}
                >
                  {i + 1}
                </Box>
                <Typography
                  variant="body2"
                  sx={{ color: theme.customColors.text.secondary, lineHeight: 1.6 }}
                >
                  {step}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              border: `1px solid ${alpha(accent, 0.3)}`,
              borderRadius: "8px",
              overflow: "hidden",
              backgroundColor: theme.customColors.background.main,
              maxWidth: 520,
            }}
          >
            <SmartScreenIllustration />
          </Box>
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mt: 0.75,
              mb: 2,
              color: theme.customColors.text.muted,
              fontStyle: "italic",
            }}
          >
            The prompt as it appears on Windows. "Run anyway" is revealed after you click "More
            info".
          </Typography>

          <Typography
            variant="body2"
            sx={{ color: theme.customColors.text.secondary, lineHeight: 1.6 }}
          >
            Read Microsoft's explanation of SmartScreen reputation:{" "}
            <Link
              href={SMARTSCREEN_DOC_URL}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: theme.customColors.status.info.text,
                fontWeight: 600,
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" },
                whiteSpace: "nowrap",
              }}
            >
              SmartScreen and app reputation
              <OpenInNewIcon sx={{ fontSize: 13, ml: 0.4, verticalAlign: "-1px" }} />
            </Link>
          </Typography>
        </Box>
      </Collapse>
    </Box>
  );
};

export default WindowsSmartScreenNotice;
